import React from 'react';
import { colors } from './constants';
import { calculateDuration } from './helpers';
import { XIcon } from './Icons';

const DischargeModal = ({ 
  show, 
  onClose, 
  selectedBedForDischarge, 
  currentCategory, 
  totalDailyCharge,
  handleDischargeBed 
}) => {
  if (!show || !selectedBedForDischarge) return null;

  const duration = calculateDuration(selectedBedForDischarge.admissionDate);
  const totalBill = totalDailyCharge * duration;

  const styles = {
    modalOverlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      backdropFilter: 'blur(4px)'
    },
    modal: {
      background: colors.cream100,
      borderRadius: '12px',
      width: '90%',
      maxWidth: '500px',
      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)'
    },
    modalHeader: {
      padding: '24px',
      borderBottom: `1px solid rgba(94, 82, 64, 0.12)`,
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    },
    modalTitle: {
      fontSize: '18px',
      fontWeight: 550,
      color: colors.slate900,
      margin: 0
    },
    closeButton: {
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      padding: '8px',
      display: 'flex',
      alignItems: 'center',
      color: colors.slate500,
      borderRadius: '6px',
      transition: 'all 0.2s ease'
    },
    modalBody: {
      padding: '24px'
    },
    infoRow: {
      display: 'flex',
      justifyContent: 'space-between',
      marginBottom: '12px',
      fontSize: '14px'
    },
    infoLabel: {
      color: colors.slate500
    },
    infoValue: {
      fontWeight: 500,
      color: colors.slate900
    },
    billSection: {
      background: colors.cream50,
      border: `1px solid rgba(94, 82, 64, 0.12)`,
      borderRadius: '8px',
      padding: '16px',
      marginTop: '16px'
    },
    billTitle: {
      fontSize: '14px',
      fontWeight: 550,
      marginBottom: '12px',
      color: colors.slate900
    },
    totalBill: {
      marginTop: '12px',
      paddingTop: '12px',
      borderTop: `2px solid rgba(94, 82, 64, 0.12)`,
      fontSize: '18px',
      fontWeight: 600,
      color: colors.teal600,
      textAlign: 'right'
    },
    modalFooter: {
      padding: '24px',
      borderTop: `1px solid rgba(94, 82, 64, 0.12)`,
      display: 'flex',
      gap: '12px',
      justifyContent: 'flex-end'
    },
    btnDanger: {
      padding: '12px 24px',
      fontSize: '14px',
      fontFamily: 'inherit',
      fontWeight: 500,
      borderRadius: '8px',
      border: 'none',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      background: colors.red500,
      color: colors.cream50
    },
    btnSecondary: {
      padding: '12px 24px',
      fontSize: '14px',
      fontFamily: 'inherit',
      fontWeight: 500,
      borderRadius: '8px',
      border: `1px solid rgba(94, 82, 64, 0.2)`,
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      background: 'transparent',
      color: colors.slate900
    }
  };

  return (
    <div style={styles.modalOverlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.modalHeader}>
          <h2 style={styles.modalTitle}>Discharge Patient</h2>
          <button 
            style={styles.closeButton}
            onClick={onClose}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(94, 82, 64, 0.12)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'none';
            }}
          >
            <XIcon />
          </button>
        </div>

        <div style={styles.modalBody}>
          <div style={styles.infoRow}>
            <span style={styles.infoLabel}>Bed ID:</span>
            <span style={styles.infoValue}>{selectedBedForDischarge.id}</span>
          </div>
          <div style={styles.infoRow}>
            <span style={styles.infoLabel}>Patient Name:</span>
            <span style={styles.infoValue}>{selectedBedForDischarge.patient}</span>
          </div>
          <div style={styles.infoRow}>
            <span style={styles.infoLabel}>Doctor:</span>
            <span style={styles.infoValue}>{selectedBedForDischarge.doctor}</span>
          </div>
          <div style={styles.infoRow}>
            <span style={styles.infoLabel}>Admission Date:</span>
            <span style={styles.infoValue}>{selectedBedForDischarge.admissionDate}</span>
          </div>
          <div style={styles.infoRow}>
            <span style={styles.infoLabel}>Days Admitted:</span>
            <span style={styles.infoValue}>{duration} days</span>
          </div>

          <div style={styles.billSection}>
            <div style={styles.billTitle}>Billing Summary</div>
            <div style={styles.infoRow}>
              <span style={styles.infoLabel}>Daily Rate:</span>
              <span style={styles.infoValue}>₹{totalDailyCharge.toLocaleString()}</span>
            </div>
            <div style={styles.infoRow}>
              <span style={styles.infoLabel}>Duration:</span>
              <span style={styles.infoValue}>{duration} days</span>
            </div>
            <div style={styles.totalBill}>
              Total: ₹{totalBill.toLocaleString()}
            </div>
          </div>
        </div>

        <div style={styles.modalFooter}>
          <button 
            style={styles.btnSecondary}
            onClick={onClose}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(94, 82, 64, 0.08)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
            }}
          >
            Cancel
          </button>
          <button 
            style={styles.btnDanger}
            onClick={handleDischargeBed}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(192, 21, 47, 0.9)';
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(192, 21, 47, 0.3)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = colors.red500;
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            Discharge Patient
          </button>
        </div>
      </div>
    </div>
  );
};

export default DischargeModal;
